import { LANGUAGE } from '@/types/commonConst';

import type { MessageMap } from '..';
import guideContent from './guide-content.md?raw';
import guideContentKr from './guide-content-kr.md?raw';

const CUSTOM_DOC_TEMPLATE_EN =
  'For example:\nCreate a Customer Intelligence Brief that synthesizes diverse customer feedback into key themes and insights. Begin with a concise Executive Summary highlighting the main sentiment trends and top issues. The body should deeply analyze 3–5 major themes, clearly revealing expectation–experience gaps and perception differences among user groups. Conclude with actionable strategic recommendations, prioritized by impact and urgency. The tone must be objective and incisive.';

const CUSTOM_DOC_TEMPLATE_KO =
  'For example:\n여러 고객 피드백 데이터를 종합하여 주요 주제와 통찰을 도출하는 Customer Intelligence Brief 를 작성하라. 문서는 간결한 Executive Summary 로 시작하여 핵심 이슈와 기조를 한눈에 보여야 한다. 본문은 3~5개의 주요 주제에 대해 심층적으로 분석하고, 고객의 기대와 실제 경험 간의 차이 또는 인식 불일치를 명확히 드러내라. 마지막으로, 실행 가능한 전략적 권고안을 우선순위별로 제시하라.';

const TOP_10_ISSUES_REPORT_TEMPLATE_EN =
  'Generate a concise analytical document that identifies and ranks the ten most frequently mentioned negative issues from customer feedback. Begin with a table that clearly presents each issue’s rank, category, volume, and severity. Follow with detailed analyses of the top three issues, including context, representative customer quotes, and impact assessment. Conclude with a summary section highlighting patterns and emerging risks. Maintain a precise, objective, and data‑driven tone.';

const TOP_10_ISSUES_REPORT_TEMPLATE_KO =
  '고객 피드백에서 가장 자주 언급된 부정적 이슈 10가지를 식별하고 순위화한 간결한 분석 보고서를 작성하라. 각 이슈의 순위, 카테고리, 언급 횟수, 심각도를 표로 명확히 제시하고, 상위 3개 이슈에 대해서는 구체적 맥락, 대표 고객 의견, 비즈니스 영향 평가를 포함한 세부 분석을 수행하라. 마지막에 주요 패턴과 잠재적 리스크를 요약하되, 어조는 객관적이고 데이터 중심적으로 유지하라.';

const FAQ_TEMPLATE_EN =
  'Analyze customer feedback data to group inquiries with the same intent, identify up to the Top 10 most frequent issues, and create a frequency-based FAQ report in Q&A format. The report should include a summary table, detailed category-wise FAQs, and a section for root cause analysis with improvement suggestions. Maintain a customer-friendly yet professional CS tone, and write exclusively in English.';
const FAQ_TEMPLATE_KO =
  '고객 문의 데이터를 분석하여 동일한 의도를 가진 문의를 그룹화하고 빈도 기준으로 상위 10개의 핵심 질문을 식별한 뒤, FAQ 형식(Q&A)으로 구성된 빈도 기반 보고서를 작성한다. 보고서는 문의 요약표, 카테고리별 세부 FAQ, 그리고 원인 분석 및 개선 제안을 포함해야 한다. 고객 친화적이면서도 전문적인 CS 톤을 유지하고, 한국어로 작성한다.';

const CHURN_SIGNAL_ANALYSIS_TEMPLATE_EN =
  'Create an actionable Churn Signal Analysis Report based on unstructured customer voice data. Start with an Executive Summary outlining churn risk severity and dominant behavioral patterns. Then classify churn risk levels and identify key churn drivers using quoted keywords and verbatims from the data. Structure the body with tables, bullet points, and concise headings to analyze each churn pattern systematically. Maintain an objective and data-grounded tone, avoiding any assumptions beyond the provided text. Finish with a practical Action Plan that prioritizes urgent issues and recommends targeted interventions.';

const CHURN_SIGNAL_ANALYSIS_TEMPLATE_KO =
  '고객 피드백을 기반으로 Churn Signal Analysis Report 를 작성하라. 서두에는 Executive Summary 을 두어 이탈 위험 수준과 주요 행동 패턴을 간결히 제시하고, 이후에는 실제 인용된 키워드와 문장을 활용해 이탈 수준별 분류와 주요 요인을 체계적으로 분석한다. 표와 불릿 포인트, 명확한 제목 구조를 활용해 각 이탈 패턴을 논리적으로 정리하되, 데이터에 없는 내용은 추론하지 않는다. 마지막에는 시급한 개선 과제와 실행 가능한 Action Plan을 포함시켜 실무에 활용할 수 있도록 구성하라.';

export const heySynclyMessages = {
  Q1: {
    [LANGUAGE.EN]: 'What are the most mentioned customer complaints?',
    [LANGUAGE.KO]: '어떤 상황에서 불만이 발생하나요?',
  },
  Q2: {
    [LANGUAGE.EN]: 'What are the most requested new products?',
    [LANGUAGE.KO]: '주로 요청되는 기능은 무엇인가요?',
  },
  Q3: {
    [LANGUAGE.EN]: 'What do our customers like about our product and service?',
    [LANGUAGE.KO]: '사람들의 반응을 제품마다 자세히 요약해주세요',
  },
  GUIDE: {
    [LANGUAGE.EN]: 'How to write prompt that works',
    [LANGUAGE.KO]: '더 유용한 답변을 얻는 방법',
  },
  CUSTOM_DOC_TEMPLATE: {
    [LANGUAGE.EN]: CUSTOM_DOC_TEMPLATE_EN,
    [LANGUAGE.KO]: CUSTOM_DOC_TEMPLATE_KO,
  },
  TOP_10_ISSUES_REPORT_TEMPLATE: {
    [LANGUAGE.EN]: TOP_10_ISSUES_REPORT_TEMPLATE_EN,
    [LANGUAGE.KO]: TOP_10_ISSUES_REPORT_TEMPLATE_KO,
  },
  FAQ_TEMPLATE: {
    [LANGUAGE.EN]: FAQ_TEMPLATE_EN,
    [LANGUAGE.KO]: FAQ_TEMPLATE_KO,
  },
  CHURN_SIGNAL_ANALYSIS_TEMPLATE: {
    [LANGUAGE.EN]: CHURN_SIGNAL_ANALYSIS_TEMPLATE_EN,
    [LANGUAGE.KO]: CHURN_SIGNAL_ANALYSIS_TEMPLATE_KO,
  },
  PROMPT_GUIDE_TITLE: {
    [LANGUAGE.EN]: 'How to write prompt that works',
    [LANGUAGE.KO]: '더 유용한 답변을 얻는 방법',
  },
  PROMPT_GUIDE_CONTENT: {
    [LANGUAGE.EN]: guideContent,
    [LANGUAGE.KO]: guideContentKr,
  },
  GPT_TEMPLATE_SUMMARY: {
    [LANGUAGE.EN]: 'Summarize for me',
    [LANGUAGE.KO]: '이 피드백들을 요약해주세요',
  },
  GPT_TEMPLATE_ACTION: {
    [LANGUAGE.EN]: 'Provide me with some suggestions regarding what to do',
    [LANGUAGE.KO]: '어떤 액션이 필요한지 제안해주세요',
  },
} as const satisfies MessageMap;
